/**
 * 图生文任务记录列表页
 * 展示当前用户所有图生文批量任务，支持分页。
 * 有未完成任务时每 5 秒自动轮询。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import {
  CheckCircle2,
  Clock,
  FileText,
  Inbox,
  Loader2,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ImageToTextTask } from '@/types/imageToTextTask'
import {
  IMAGE_TO_TEXT_TASK_STATUS_LABELS,
  IMAGE_TO_TEXT_TOOL_TYPE_LABELS,
  isImageToTextTaskTerminal,
} from '@/types/imageToTextTask'
import { listImageToTextTasks } from '@/services/imageToTextTaskService'

const POLL_INTERVAL = 5000
const PAGE_SIZE = 20

function StatusIcon({ status }: { status: ImageToTextTask['status'] }): ReactElement {
  if (status === 'completed')
    return <CheckCircle2 className="h-4 w-4 text-emerald-500" />
  if (status === 'failed') return <XCircle className="h-4 w-4 text-destructive" />
  if (status === 'partial_failed')
    return <AlertTriangle className="h-4 w-4 text-amber-500" />
  if (status === 'running')
    return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
  return <Clock className="h-4 w-4 text-muted-foreground/60" />
}

function TaskCard({ task }: { task: ImageToTextTask }): ReactElement {
  const statusInfo = IMAGE_TO_TEXT_TASK_STATUS_LABELS[task.status]
  const toolLabel =
    task.tool_types !== undefined && task.tool_types.length > 0
      ? task.tool_types.map((t) => IMAGE_TO_TEXT_TOOL_TYPE_LABELS[t] ?? t).join(' · ')
      : task.tool_type !== undefined
        ? (IMAGE_TO_TEXT_TOOL_TYPE_LABELS[task.tool_type] ?? 'AI 图像分析')
        : 'AI 图像分析'

  return (
    <Link to={`/records/tools/${task.id}`} className="block">
      <div className="group flex items-center gap-4 rounded-2xl border bg-card px-4 py-3.5 shadow-sm transition-shadow hover:shadow-md">
        {/* 状态图标 */}
        <StatusIcon status={task.status} />

        {/* 主体信息 */}
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium">{toolLabel}</span>
            <span className={`text-xs font-semibold ${statusInfo.color}`}>
              {statusInfo.text}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            共 {task.total_count.toLocaleString()} 项 · 成功{' '}
            {task.completed_count.toLocaleString()} · 失败{' '}
            {task.failed_count.toLocaleString()}
            {task.remark !== '' && ` · ${task.remark}`}
          </p>
        </div>

        {/* 时间 */}
        <span className="flex-shrink-0 text-xs text-muted-foreground">
          {new Date(task.created_at).toLocaleString('zh-CN', {
            month: 'numeric',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>

        {/* 箭头暗示 */}
        <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground/60" />
      </div>
    </Link>
  )
}

export function ImageToTextRecordsPage(): ReactElement {
  const [tasks, setTasks] = useState<ImageToTextTask[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const fetchData = useCallback(
    async (p: number): Promise<void> => {
      try {
        const data = await listImageToTextTasks({ page: p, pageSize: PAGE_SIZE })
        setTasks(data.items)
        setTotal(data.total)
        setError(null)
      } catch (err) {
        setError(err instanceof Error ? err.message : '加载失败')
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    setLoading(true)
    void fetchData(page)
  }, [page, fetchData])

  // 有未完成任务时轮询
  useEffect(() => {
    const hasRunning = tasks.some((t) => !isImageToTextTaskTerminal(t.status))
    if (!hasRunning) return

    timerRef.current = setTimeout(() => {
      void fetchData(page)
    }, POLL_INTERVAL)

    return (): void => {
      if (timerRef.current !== null) clearTimeout(timerRef.current)
    }
  }, [tasks, page, fetchData])

  return (
    <div className="space-y-6">
      {/* 头部 */}
      <div className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">工具任务记录</h1>
        <p className="text-sm text-muted-foreground">
          查看商品标题生成、反推提示词、侵权检测的历史任务
        </p>
      </div>

      {/* 内容 */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error !== null ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-20 text-muted-foreground">
          <Inbox className="h-10 w-10 opacity-30" />
          <p className="text-sm">还没有任务记录</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/tools/ai-analysis">去使用工具</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} />
          ))}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => { setPage((p) => Math.max(1, p - 1)) }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[5rem] text-center text-sm text-muted-foreground">
            第 {String(page)} / {String(totalPages)} 页
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPages}
            onClick={() => { setPage((p) => Math.min(totalPages, p + 1)) }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}
